import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'context/AppContext';
import { parseIsoDate, formatShortDate } from 'helpers/DateHelper';
import { distinct, flattenArray } from 'helpers/ArrayHelper';
import { calculateChartData, getCapacityUtilizationForDate, getAllColumnsForProperty } from  'logic/capacityUtilizationLogic';
import { makeStyles } from 'styles/util';
import strings from 'localization/strings';
import capacityUtilizationProperties from 'enums/capacityUtilizationProperties';
import { getBusinessOrPrivateName } from 'helpers/ActorHelper';
import { defaultCompare } from 'helpers/SortHelper';

import Box from '@material-ui/core/Box';
import CapacityUtilizationChart from './CapacityUtilizationChart';
import Form from 'form/Form';
import { TextField, Checkboxes } from 'mui-rff';
import MenuItem from '@material-ui/core/MenuItem';
import AdminTable from 'components/adminTable/AdminTable';
import ShortDate from 'common/ShortDate';
import { OnChange } from 'react-final-form-listeners';
import { Link } from 'react-router-dom';

const useStyles = makeStyles(({ colors }) => ({
    chartContainer: {
        width: '100%'
    },
    compactCheckbox: {
        marginTop: 0,
        marginBottom: 0,
        paddingTop: 0,
        paddingBottom: 0
    },
    selectAll: {
        fontSize: '0.875rem'
    },
    header: {
        backgroundColor: colors.mediumLightGrey
    }
}));

const CapacityUtilization = ({ storageSites, fromDate, toDate, selectedProperty: initialSelectedProperty, showStorageSiteList = false, showOwner = false, ownerLinkProvider, storageSiteColumnRenderer, onSelectedPropertyChange }) => {
    const classes = useStyles();
    const { appContext } = useAppContext();

    const [chartData, setChartData] = useState(undefined);
    const [chartWidth, setChartWidth] = useState(undefined);
    const [chartFormInitialValues, setChartFormInitialValues] = useState(undefined);
    const [sortingParameters, setSortingParameters] = useState({});
    const [selectedProperty, setSelectedProperty] = useState(initialSelectedProperty ?? capacityUtilizationProperties.bookedCount.key);

    // a hack to make the select/unselect all checkbox efficient
    const [stateForm, setStateForm] = useState(undefined);
    const [preventUpdates, setPreventUpdates] = useState(false);

    const chartContainerRef = useRef();

    useEffect(() => {
        setPreventUpdates(true);
        initializeChartFormValues(storageSites);
    }, [storageSites]);

    useEffect(() => {
        if(chartFormInitialValues) {
            setPreventUpdates(false);
            setChartData(calculateChartData(storageSites, fromDate, toDate, appContext));
        }
    }, [chartFormInitialValues]);

    useEffect(() => {
        if(chartContainerRef.current) {
            setChartWidth(chartContainerRef.current.offsetWidth);
        }
    }, [chartContainerRef]);

    useEffect(() => {
        if(stateForm) {
            const formValues = stateForm.getState().values;
            stateForm.batch(() => {
                storageSites.forEach(storageSite => {
                    stateForm.change(getStorageSiteFieldName(storageSite), formValues.selectAll);
                });
            });
            recalculateChart(stateForm.getState().values);
            setStateForm(undefined);
            setPreventUpdates(false);
        }
    }, [stateForm]);

    const initializeChartFormValues = ss => {
        const iv = {
            selectedProperty
        };
        iv.selectAll = true;
        ss.forEach(storageSite => {
            const key = getStorageSiteFieldName(storageSite);
            if(chartFormInitialValues) {
                iv[key] = chartFormInitialValues[key];
            }
            iv[key] = iv[key] ?? true;
            iv.selectAll = iv.selectAll && iv[key];
        });
        setChartFormInitialValues(iv);
    };

    const recalculateChart = (formValues, usePreventUpdates) => {
        if(usePreventUpdates && preventUpdates) {
            return;
        }
        const filteredStorageSites = storageSites.filter(storageSite => formValues[getStorageSiteFieldName(storageSite)]);
        setChartData(calculateChartData(filteredStorageSites, fromDate, toDate, appContext));
    };

    const selectAll = form => {
        setPreventUpdates(true);
        setStateForm(form); // useEffect will take care of the rest
    };

    const getStorageSiteFieldName = storageSite => `storageSite_${storageSite.id}`;

    const handleChartFormSubmit = () => {};

    const renderOwner = storageSite => {
        const name = getBusinessOrPrivateName(storageSite.ownerActor);
        if(ownerLinkProvider) {
            return (
                <Link to={ownerLinkProvider(storageSite)}>{name}</Link>
            );
        }
        return name;
    };

    const getLastPriceChangeDate = storageSite => {
        const dates = distinct(flattenArray(storageSite.storageGroups.map(sg => sg.timePeriodPrices.map(o => o.startDate).filter(o => o))));
        if(dates.length === 0) {
            return undefined;
        }
        dates.sort();
        return parseIsoDate(dates[dates.length - 1]);
    };

    // used to format value for tooltip and in table
    const formatValue = (property, storageSite) => {
        const capacityUtilization = getCapacityUtilizationForDate(storageSite, toDate);
        if(!capacityUtilization) {
            return undefined;
        }
        return property.formatValueFromObject(capacityUtilization, appContext);
    };

    const getNumericValue = (property, storageSite) => {
        const capacityUtilization = getCapacityUtilizationForDate(storageSite, toDate);
        if(!capacityUtilization) {
            return undefined;
        }
        return property.getNumericValue(capacityUtilization, appContext);
    };

    const getColumns = property => {
        const columns = [
            {
                title: (
                    <Checkboxes
                        className={classes.compactCheckbox}
                        name="selectAll"
                        data={
                            { label: <Box component="span" className={classes.selectAll}>{strings.selectAll}</Box>, value: true }
                        }
                    />
                ),
                sortable: false,
                id: 'selectAll',
                render: storageSite => (
                    <Checkboxes
                        className={classes.compactCheckbox}
                        name={getStorageSiteFieldName(storageSite)}
                        data={
                            { value: true }
                        }
                    />
                )
            },
            {
                title: strings.storageSite,
                id: 'title',
                render: storageSite => storageSiteColumnRenderer ? storageSiteColumnRenderer(storageSite) : storageSite.title
            },
            showOwner
                ? {
                    title: strings.owner,
                    id: 'owner',
                    render: storageSite => renderOwner(storageSite)
                }
                : undefined,
            {
                title: strings.lastPriceChange,
                id: 'lastPriceChange',
                render: storageSite => <ShortDate value={getLastPriceChangeDate(storageSite)}/>,
                customSort: (a, b) => defaultCompare(getLastPriceChangeDate(a), getLastPriceChangeDate(b))
            }
        ].filter(o => o);
        getAllColumnsForProperty(property).forEach(p => {
            columns.push({
                title: strings.formatString(strings.propertyXAtDateY, p.title, formatShortDate(toDate, appContext)),
                id: property.key,
                align: 'right',
                render: storageSite => formatValue(p, storageSite),
                customSort: (a, b) => defaultCompare(getNumericValue(p, a), getNumericValue(p, b))
            });
        });

        return columns;
    };

    const handleSort = sp => {
        setSortingParameters(sp);
    };

    // need to use React.memo for efficient rendering
    const ChartContainerRenderer = React.memo(
        ({ form, values }) => {
            const property = capacityUtilizationProperties[values.selectedProperty];
            const columns = getColumns(property);
            return (
                <>
                    <CapacityUtilizationChart
                        width={chartWidth}
                        height={400}
                        data={chartData}
                        property={property}
                    />
                    <TextField
                        select
                        name="selectedProperty"
                        label={strings.capacityUtilizationPropertyLabel}
                        variant="outlined"
                    >
                        {
                            Object.values(capacityUtilizationProperties).map(item =>
                                <MenuItem key={item.key} value={item.key}>{item.title}</MenuItem>
                            )
                        }
                    </TextField>
                    <OnChange name="selectedProperty">
                        {
                            newValue => {
                                setSelectedProperty(newValue);
                                if(onSelectedPropertyChange) {
                                    onSelectedPropertyChange(newValue);
                                }
                                recalculateChart({...values, property: newValue });
                            }
                        }
                    </OnChange>

                    {
                        showStorageSiteList &&
                        (
                            <>
                                <AdminTable
                                    items={storageSites}
                                    columns={columns}
                                    sortByColumn={columns.find(c => c.id === sortingParameters.sortByColumn)}
                                    sortDirection={sortingParameters.sortDirection}
                                    onSort={handleSort}

                                />
                                <OnChange name="selectAll">
                                    {newValue => selectAll(form, newValue)}
                                </OnChange>
                                {
                                    storageSites.map(storageSite => (
                                        <OnChange key={storageSite.id} name={getStorageSiteFieldName(storageSite)}>
                                            {newValue => recalculateChart({ ...values, [getStorageSiteFieldName(storageSite)]: newValue }, true)}
                                        </OnChange>
                                    ))
                                }
                            </>
                        )
                    }
                </>
            );
        },
        // propsAreEqual
        (previousProps, nextProps) => {
            const previousValue = getFormString(previousProps.form?.getState().values);
            const nextValue = getFormString(nextProps.form?.getState().values);
            return previousValue === nextValue;
        }
    );

    ChartContainerRenderer.propTypes = {
        form: PropTypes.object.isRequired,
        values: PropTypes.object.isRequired
    };

    const getFormString = state =>  {
        const items = [state.selectedProperty];
        storageSites.forEach(storageSite => {
            if(state[getStorageSiteFieldName(storageSite)]) {
                items.push(storageSite.id);
            }
        });
        return items.join(',');
    };

    return (
        <Box className={classes.chartContainer} ref={chartContainerRef}>
            {
                chartData &&
                (
                    <Form
                        component={ChartContainerRenderer}
                        initialValues={chartFormInitialValues}
                        onSubmit={handleChartFormSubmit}
                    >
                        {() => {} /* dummy func to avoid warnings */}
                    </Form>
                )
            }
        </Box>
    );
};

CapacityUtilization.propTypes = {
    storageSites: PropTypes.array.isRequired,
    fromDate: PropTypes.instanceOf(Date).isRequired,
    toDate: PropTypes.instanceOf(Date).isRequired,
    selectedProperty: PropTypes.string,
    showStorageSiteList: PropTypes.bool,
    showOwner: PropTypes.bool,
    ownerLinkProvider: PropTypes.func,
    storageSiteColumnRenderer: PropTypes.func,
    onSelectedPropertyChange: PropTypes.func
};

export default CapacityUtilization;
